<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NButton, NDrawer, NDrawerContent, NInput, NSelect } from 'naive-ui'
import { useMemoryEntriesStore, GLOBAL_PROJECT, humanizeProject } from '../stores/memory_entries'
import { useIsMobile } from '../composables/useIsMobile'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// New-entry drawer: a form to add a memory entry from the UI. Reading,
// moving and deleting an entry happen on its own page
// (MemoryEntryPage). The write goes through the store action (which
// re-fetches the list), so the drawer just drives it and closes.

const show = defineModel<boolean>('show', { required: true })
const props = defineProps<{
  // Existing scope slugs (incl. 'global') for the scope select.
  projects: string[]
}>()
const emit = defineEmits<{ (e: 'changed'): void }>()

useRestoreFocusOnClose(show)

const store = useMemoryEntriesStore()
const isMobile = useIsMobile()
const drawerWidth = computed<number | string>(() => (isMobile.value ? '100%' : 560))

const actionError = ref<string | null>(null)
const submitting = ref(false)

function scopeLabel(slug: string): string {
  return slug === GLOBAL_PROJECT ? 'Global' : humanizeProject(slug)
}
const projectOptions = computed(() => props.projects.map((p) => ({ value: p, label: scopeLabel(p) })))

async function run(action: () => Promise<void>): Promise<void> {
  submitting.value = true
  actionError.value = null
  try {
    await action()
    emit('changed')
    show.value = false
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : 'Action failed'
  } finally {
    submitting.value = false
  }
}

const TYPE_OPTIONS = [
  { value: 'feedback', label: 'Feedback' },
  { value: 'reference', label: 'Reference' },
  { value: 'project', label: 'Project note' },
]
const form = ref({ name: '', type: 'feedback', project: GLOBAL_PROJECT, description: '', body: '' })

watch(show, (open) => {
  if (open) {
    form.value = { name: '', type: 'feedback', project: GLOBAL_PROJECT, description: '', body: '' }
    actionError.value = null
  }
})

const canCreate = computed(
  () =>
    form.value.name.trim() !== '' &&
    form.value.project.trim() !== '' &&
    form.value.description.trim() !== '' &&
    form.value.body.trim() !== '',
)

function onCreate(): void {
  if (!canCreate.value) return
  void run(() =>
    store.createEntry({
      name: form.value.name.trim(),
      type: form.value.type,
      project: form.value.project.trim(),
      description: form.value.description.trim(),
      body: form.value.body,
    }),
  )
}
</script>

<template>
  <NDrawer v-model:show="show" :width="drawerWidth" placement="right">
    <NDrawerContent title="New memory entry" closable data-testid="memory-drawer">
      <p v-if="actionError" class="text-red-300 text-sm m-0 mb-3" data-testid="memory-drawer-error">
        {{ actionError }}
      </p>

      <div class="flex flex-col gap-3" data-testid="memory-create-form">
        <label class="flex flex-col gap-1 text-xs text-white/50">
          Name (slug)
          <NInput
            v-model:value="form.name"
            size="small"
            placeholder="kebab-or-snake-case"
            data-testid="memory-create-name"
          />
        </label>
        <div class="flex gap-3">
          <label class="flex-1 flex flex-col gap-1 text-xs text-white/50">
            Type
            <NSelect v-model:value="form.type" size="small" :options="TYPE_OPTIONS" />
          </label>
          <label class="flex-1 flex flex-col gap-1 text-xs text-white/50">
            Scope
            <NSelect
              v-model:value="form.project"
              size="small"
              filterable
              tag
              :options="projectOptions"
              data-testid="memory-create-scope"
            />
          </label>
        </div>
        <label class="flex flex-col gap-1 text-xs text-white/50">
          Description (one-line hook)
          <NInput v-model:value="form.description" size="small" />
        </label>
        <label class="flex flex-col gap-1 text-xs text-white/50">
          Body
          <NInput v-model:value="form.body" type="textarea" :rows="8" placeholder="The note itself (markdown)." />
        </label>
        <NButton
          type="primary"
          :disabled="!canCreate || submitting"
          :loading="submitting"
          data-testid="memory-create-submit"
          @click="onCreate"
          >Create entry</NButton
        >
      </div>
    </NDrawerContent>
  </NDrawer>
</template>
